import { toast } from '@heidi/design-system/toast';
import {
  COLLECTIONS_QUERY_KEY,
  DOCUMENTS_AND_JOBS_KEY,
  GET_USER_DOCUMENTS,
} from '@heidi/evidence/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { saveArtefactMessages } from '../../../data/projects/saveArtefactMessages';
import { saveExportAsArtefact } from './saveExportAsArtefact';

export type TrackWorkArtifactCreated = (props: {
  project_id?: string;
  agent_run_id?: string;
  artifact_type?: string;
  outcome?: 'succeeded' | 'failed';
}) => void;

export interface SaveExportAsArtefactInput {
  url: string;
  filename: string;
}

export function useSaveExportAsArtefact(trackCreated: TrackWorkArtifactCreated) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const invalidateLibraryQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [GET_USER_DOCUMENTS] }),
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_AND_JOBS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] }),
    ]);
  }, [queryClient]);

  const save = useCallback(
    async ({ url, filename }: SaveExportAsArtefactInput) => {
      setIsSaving(true);
      try {
        await saveExportAsArtefact({ url, filename });
        await invalidateLibraryQueries();
        trackCreated({
          artifact_type: 'library_artefact',
          outcome: 'succeeded',
        });
        toast({
          title: intl.formatMessage(saveArtefactMessages.savedTitle),
          description: intl.formatMessage(saveArtefactMessages.savedDescription),
        });
      } catch (error) {
        trackCreated({
          artifact_type: 'library_artefact',
          outcome: 'failed',
        });
        toast({
          tone: 'destructive',
          title: intl.formatMessage(saveArtefactMessages.errorTitle),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage(saveArtefactMessages.errorDescription),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [intl, invalidateLibraryQueries, trackCreated]
  );

  return { save, isSaving };
}
